import { articleApi, companyApi, CompanyVo, ECompanyCommonConfigType } from '@/axios/company';
import styles from './index.module.less';
import { Button, Flex, Form, Input, message, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { openCompanyInformationModal, openEditCompetitorModal } from '../Article';
import { useRequest } from 'ahooks';
import AccessButton from '@/components/AccessButton';
import CommonModalFactory from '@/plugins/ModalFactory';
import CompanyImageGallery from './CompanyImageGallery';
const { Paragraph, Text } = Typography;
const MAX_COMPETITOR_COMPARE_COUNT = 10;

const CompanyInformation = ({
  companyInfo,
  refreshCompanyInfo,
}: {
  companyInfo?: CompanyVo;
  refreshCompanyInfo?: () => void;
}) => {
  const [preWrap, setPreWrap] = useState(false);
  // 竞品列表
  const { data: competitors, runAsync: refreshCompetitorList } = useRequest(() =>
    articleApi.listCompetitors(companyInfo?.companyId || '')
  );
  const { data: competitorCompareConfig, runAsync: refreshCompetitorCompareConfig } = useRequest(() =>
    companyApi.getCommonConfigList({
      companyId: companyInfo?.companyId || '',
      configType: ECompanyCommonConfigType.CompetitorCompare,
    })
  );
  // 核心词
  const { data: coreWords } = useRequest(() => companyApi.getWordInfo(companyInfo?.companyId || ''));
  const competitorCompareList = competitorCompareConfig?.values || [];
  return (
    <div className={styles.sections}>
      <div className={styles.section}>
        <Flex vertical gap={20}>
          <div className={styles.word_type}>企业资料:</div>
          {companyInfo?.information && (
            <Paragraph
              ellipsis={{
                rows: 8,
                expandable: 'collapsible',
                onExpand: (_, { expanded }) => {
                  setPreWrap(expanded);
                },
              }}
              className={styles.word_text}
              style={{ whiteSpace: preWrap ? 'pre-wrap' : 'unset', width: '100%' }}
            >
              {companyInfo?.information}
            </Paragraph>
          )}
        </Flex>
        <AccessButton permission={['company:information:edit']}>
          <Button
            type="primary"
            onClick={() => {
              openCompanyInformationModal(
                {
                  content: companyInfo?.information || '',
                  companyId: companyInfo?.companyId || '',
                },
                {
                  width: 1000,
                  title: '编辑企业资料',
                },
                () => {
                  refreshCompanyInfo?.();
                }
              );
            }}
          >
            编辑企业资料
          </Button>
        </AccessButton>
      </div>
            <CompanyImageGallery companyId={companyInfo?.companyId || ''} />
      <div className={styles.section}>
        <Flex vertical gap={20} style={{ flex: '1', overflow: 'hidden' }}>
          <div>竞品({(competitors || []).length})：</div>
          <Flex gap={4} vertical style={{ width: '100%' }}>
            {(competitors || []).map((competitor, index) => (
              <Flex gap={8} key={index} className={styles.word_text}>
                <div>{index + 1}</div>
                <div>【{competitor.competitorName}】</div>
                <div
                  style={{
                    flex: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <Text
                    ellipsis={{
                      tooltip: competitor.introduce,
                    }}
                  >
                    {competitor.introduce}
                  </Text>
                </div>
              </Flex>
            ))}
          </Flex>
        </Flex>
        <AccessButton permission={['company:information:edit']}>
          <Button
            type="primary"
            onClick={() => {
              openEditCompetitorModal(
                {
                  competitors: competitors || [],
                  coreWords: coreWords?.coreWords.words || [],
                  companyId: companyInfo?.companyId || '',
                },
                undefined,
                (_) => {
                  refreshCompetitorList();
                }
              );
            }}
          >
            编辑竞品
          </Button>
        </AccessButton>
      </div>
      <div className={styles.section}>
        <Flex vertical gap={20} style={{ flex: '1', overflow: 'hidden' }}>
          <div>竞品对比({competitorCompareList.length})：</div>
          <Flex gap={4} vertical style={{ width: '100%' }}>
            {competitorCompareList.length === 0 && <div className={styles.word_text}>暂无竞品对比数据</div>}
            {competitorCompareList.map((competitor, index) => (
              <Flex gap={8} key={`${competitor}-${index}`} className={styles.word_text}>
                <div>{`竞争${index + 1}:`}</div>
                <div>{competitor}</div>
              </Flex>
            ))}
          </Flex>
        </Flex>
        <AccessButton permission={['company:information:edit']}>
          <Button
            type="primary"
            onClick={() => {
              openEditCompetitorCompareModal(
                {
                  companyId: companyInfo?.companyId || '',
                  competitors: competitorCompareList,
                },
                undefined,
                () => {
                  refreshCompetitorCompareConfig();
                }
              );
            }}
          >
            编辑竞品对比
          </Button>
        </AccessButton>
      </div>
    </div>
  );
};
export default CompanyInformation;

export const openEditCompetitorCompareModal = CommonModalFactory<{
  companyId: string;
  competitors?: string[];
}>(
  {
    title: '竞品对比',
    width: 700,
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '16px',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const competitorValues = Form.useWatch('competitors', form);
    const hasCompetitorInput = (competitorValues || []).some((item: string) => item?.trim());

    useEffect(() => {
      form.setFieldsValue({
        competitors: (data?.competitors || []).slice(0, MAX_COMPETITOR_COMPARE_COUNT),
      });
    }, [data?.competitors, form]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const competitors = (values.competitors || [])
          .slice(0, MAX_COMPETITOR_COMPARE_COUNT)
          .map((item: string) => item?.trim() ?? '');
        await companyApi.updateCommonConfigList({
          companyId: data?.companyId || '',
          configType: ECompanyCommonConfigType.CompetitorCompare,
          values: competitors,
        });
        message.success('保存成功');
      });
    }, [data?.companyId, form, setOkFn]);
    return (
      <Form form={form} layout="vertical">
        <Form.List name="competitors">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Form.Item key={field.key} required={false}>
                  <Flex gap={8} align="center">
                    <Form.Item
                      {...field}
                      validateTrigger={['onChange', 'onBlur']}
                      noStyle
                    >
                      <Input placeholder="请输入竞品名称，置空则使用排名靠前的竞品" />
                    </Form.Item>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(field.name);
                      }}
                    />
                  </Flex>
                </Form.Item>
              ))}
              {fields.length < MAX_COMPETITOR_COMPARE_COUNT && (
                <Form.Item>
                  <Button type="dashed" onClick={() => add('')} block>
                    添加竞品对比
                  </Button>
                </Form.Item>
              )}
              {!hasCompetitorInput && (
                <div style={{ color: '#B8B8B8', fontSize: 12, lineHeight: '20px', marginTop: -8 }}>
                  请输入竞品名称，置空则使用排名靠前的竞品
                </div>
              )}
            </>
          )}
        </Form.List>
      </Form>
    );
  }
);
